import React, { useCallback, useEffect, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { CheckCircle2, Copy, ExternalLink, Loader2, Save } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import WhatsAppPanelShell from './WhatsAppPanelShell';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import type { WhatsAppTemplate } from '../../models/MasterAdmin/whatsappCampaign';
import {
  fetchWhatsAppHubSettings,
  fetchWhatsAppTemplateList,
  fetchWhatsAppWebhookEvents,
  updateWhatsAppHubSettings,
} from '../../services/MasterAdmin/whatsappHubService';
import type { WhatsAppHubSettings, WhatsAppWebhookEventStats } from '../../models/MasterAdmin/whatsappHub';

function CopyField({ label, value }: { label: string; value: string }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <div className="mt-1 flex items-start gap-2">
        <p className="min-w-0 flex-1 break-all font-mono text-xs text-gray-900 dark:text-gray-100">{value}</p>
        <button
          type="button"
          onClick={() => void copy()}
          className="shrink-0 rounded-lg border border-gray-200 bg-white p-2 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
          aria-label={`Copy ${label}`}
        >
          <Copy className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function StatusPill({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
        ok ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
      }`}
    >
      <CheckCircle2 className="h-3.5 w-3.5" />
      {label}: {ok ? 'Yes' : 'No'}
    </span>
  );
}

const selectClass =
  'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900';

const WhatsAppSettingsPanel: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<WhatsAppHubSettings | null>(null);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [ivrTemplateName, setIvrTemplateName] = useState('');
  const [webhookStats, setWebhookStats] = useState<WhatsAppWebhookEventStats | null>(null);
  const [webhookEvents, setWebhookEvents] = useState<
    Awaited<ReturnType<typeof fetchWhatsAppWebhookEvents>>['data']['events']
  >([]);
  const [webhookLoading, setWebhookLoading] = useState(false);

  const loadWebhookEvents = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setWebhookLoading(true);
    try {
      const res = await fetchWhatsAppWebhookEvents(30);
      setWebhookStats(res.data.stats);
      setWebhookEvents(res.data.events || []);
    } catch {
      if (!options?.silent) {
        setWebhookStats(null);
        setWebhookEvents([]);
      }
    } finally {
      if (!options?.silent) setWebhookLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, templatesRes] = await Promise.all([
        fetchWhatsAppHubSettings(),
        fetchWhatsAppTemplateList(),
      ]);
      setSettings(settingsRes.data);
      setEnabled(Boolean(settingsRes.data.enabled));
      setIvrTemplateName(settingsRes.data.ivrTemplateName || '');
      setTemplates(templatesRes.data || []);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to load WhatsApp settings'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    void loadWebhookEvents();
  }, [load, loadWebhookEvents]);

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      void loadWebhookEvents({ silent: true });
    };
    const timer = window.setInterval(refresh, 20000);
    return () => window.clearInterval(timer);
  }, [loadWebhookEvents]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateWhatsAppHubSettings({
        enabled,
        ivrTemplateName: ivrTemplateName.trim() || null,
      });
      setSettings((prev) => (prev ? { ...prev, ...res.data } : prev));
      toast.success('Settings saved');
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const selectedTemplate = templates.find((t) => t.name === ivrTemplateName);

  if (loading) {
    return (
      <WhatsAppPanelShell title="Settings" description="WhatsApp & Interakt configuration">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading...
        </div>
      </WhatsAppPanelShell>
    );
  }

  return (
    <WhatsAppPanelShell
      title="Settings"
      description="Webhook URLs, integration status, and WhatsApp module toggles."
      actions={
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-lg bg-[#25D366] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1da851] disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save
        </button>
      }
    >
      <div className="mx-auto max-w-5xl space-y-6">
        <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Module status</h3>
          <div className="mt-4 flex flex-wrap gap-2">
            <StatusPill ok={Boolean(settings?.globallyEnabled)} label="Env WHATSAPP_ENABLED" />
            <StatusPill ok={Boolean(settings?.interaktConfigured)} label="Interakt API key" />
            <StatusPill ok={Boolean(settings?.webhookSecretConfigured)} label="Webhook secret" />
            <StatusPill ok={Boolean(settings?.rcs?.enabled || settings?.rcsEnabled)} label="RCS enabled" />
          </div>
          <label className="mt-4 flex cursor-pointer items-center justify-between gap-4 rounded-lg border border-gray-200 p-3 dark:border-gray-600">
            <div>
              <span className="block text-sm font-medium text-gray-900 dark:text-white">WhatsApp sending enabled</span>
              <span className="text-xs text-gray-500">Master toggle for transactional, IVR, inbox, and campaigns.</span>
            </div>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-5 w-5 rounded border-gray-300 text-[#25D366]"
            />
          </label>
        </section>

        {settings?.rcs && (
          <section className="rounded-xl border border-violet-200 bg-violet-50/60 p-4 dark:border-violet-900/40 dark:bg-violet-950/20 sm:p-5">
            <h3 className="text-sm font-semibold text-violet-900 dark:text-violet-200">Interakt RCS</h3>
            <p className="mt-1 text-xs text-violet-800/80 dark:text-violet-300/80">{settings.rcs.note}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <StatusPill ok={Boolean(settings.rcs.enabled)} label="INTERAKT_RCS_ENABLED" />
              <StatusPill ok={Boolean(settings.rcs.ready)} label="Template configured" />
              <StatusPill ok={Boolean(settings.rcs.assistantId)} label="Assistant ID" />
            </div>
            {settings.rcs.templateName && (
              <p className="mt-3 font-mono text-xs text-gray-700 dark:text-gray-200">
                Default template: {settings.rcs.templateName}
              </p>
            )}
            {settings.rcs.blockers?.length ? (
              <ul className="mt-2 list-inside list-disc text-xs text-amber-800 dark:text-amber-200">
                {settings.rcs.blockers.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            ) : null}
            {settings.rcs.interaktSetup?.length ? (
              <ol className="mt-3 list-decimal space-y-1 pl-4 text-xs text-gray-700 dark:text-gray-300">
                {settings.rcs.interaktSetup.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            ) : null}
          </section>
        )}

        <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Interakt webhook (prod only)</h3>
          <p className="mt-1 text-xs text-gray-500">{settings?.setupNotes}</p>
          <div className="mt-4 space-y-3">
            {settings?.webhookUrl && <CopyField label="Webhook URL — paste in Interakt" value={settings.webhookUrl} />}
            {settings?.devMirrorUrl && (
              <CopyField label="Dev mirror (auto-forwarded from prod)" value={settings.devMirrorUrl} />
            )}
          </div>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Webhook activity</h3>
              <p className="mt-1 text-xs text-gray-500">
                Recent events received by this backend instance. Interakt hits prod only; prod mirrors to dev. Reply
                from your phone and look for <code className="font-mono">message_received</code>.
              </p>
            </div>
            <button
              type="button"
              onClick={() => void loadWebhookEvents()}
              disabled={webhookLoading}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-xs dark:border-gray-600 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-3.5 w-3.5 ${webhookLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs dark:bg-gray-900/50">
              <p className="text-gray-500">Last 24h events</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{webhookStats?.last24h ?? '—'}</p>
            </div>
            <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs dark:bg-gray-900/50">
              <p className="text-gray-500">Customer replies (24h)</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {webhookStats?.incomingLast24h ?? '—'}
              </p>
            </div>
            <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs dark:bg-gray-900/50">
              <p className="text-gray-500">Delivery updates (24h)</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {webhookStats?.deliveryLast24h ?? '—'}
              </p>
            </div>
            <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs dark:bg-gray-900/50">
              <p className="text-gray-500">Signature failures (24h)</p>
              <p
                className={`text-lg font-semibold ${
                  (webhookStats?.signatureFailuresLast24h || 0) > 0
                    ? 'text-amber-700 dark:text-amber-300'
                    : 'text-gray-900 dark:text-white'
                }`}
              >
                {webhookStats?.signatureFailuresLast24h ?? '—'}
              </p>
            </div>
          </div>
          {webhookStats?.lastIncomingAt && (
            <p className="mt-3 text-xs text-green-700 dark:text-green-300">
              Last customer reply: {new Date(webhookStats.lastIncomingAt).toLocaleString()}
            </p>
          )}
          {!webhookStats?.lastIncomingAt && webhookStats?.lastEventAt && (
            <p className="mt-3 text-xs text-gray-500">
              Last webhook: {new Date(webhookStats.lastEventAt).toLocaleString()} — no inbound replies yet.
            </p>
          )}
          {!webhookStats?.lastEventAt && (
            <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100">
              No webhooks received on this backend yet. Confirm the prod URL is in Interakt,{' '}
              <code className="font-mono">message_received</code> is enabled, and webhook secret matches GCP secret.
            </p>
          )}
          {webhookEvents.length > 0 && (
            <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-800/80">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Time</th>
                    <th className="px-3 py-2 text-left font-medium">Type</th>
                    <th className="px-3 py-2 text-left font-medium">Phone</th>
                    <th className="px-3 py-2 text-left font-medium">Status</th>
                    <th className="px-3 py-2 text-left font-medium">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {webhookEvents.map((ev) => (
                    <tr key={ev.id} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="whitespace-nowrap px-3 py-2 text-gray-500">
                        {new Date(ev.at).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 font-mono">{ev.type}</td>
                      <td className="px-3 py-2">{ev.phone || '—'}</td>
                      <td className="px-3 py-2">
                        <span
                          className={
                            ev.signatureValid === false
                              ? 'text-amber-700 dark:text-amber-300'
                              : ev.processed
                                ? 'text-green-700 dark:text-green-300'
                                : 'text-gray-600 dark:text-gray-400'
                          }
                        >
                          {ev.status}
                        </span>
                      </td>
                      <td className="max-w-xs truncate px-3 py-2 text-gray-500">{ev.detail || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">IVR WhatsApp (Exotel digit 1)</h3>
          <p className="mt-1 text-xs text-gray-500">
            When callers press 1 on the Exotel IVR, Vedika sends the selected template as-is (no dynamic link). Enable
            IVR in Exotel Calling → Settings. Use a template with fixed text only, or variables you handle elsewhere.
          </p>
          <div className="mt-4 space-y-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">IVR template</label>
              {templates.length === 0 ? (
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100">
                  No active templates yet.{' '}
                  <Link to="/operations/whatsapp/templates" className="font-medium underline">
                    Register a template
                  </Link>{' '}
                  first, then select it here.
                </p>
              ) : (
                <>
                  <select
                    value={ivrTemplateName}
                    onChange={(e) => setIvrTemplateName(e.target.value)}
                    className={selectClass}
                  >
                    <option value="">— Select template —</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.name}>
                        {t.displayName || t.name} ({t.name})
                      </option>
                    ))}
                  </select>
                  {selectedTemplate?.bodyPreview && (
                    <p className="mt-2 rounded-lg bg-gray-50 px-3 py-2 font-mono text-xs text-gray-600 dark:bg-gray-900/50 dark:text-gray-400">
                      {selectedTemplate.bodyPreview}
                    </p>
                  )}
                </>
              )}
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <Link
                to="/operations/whatsapp/templates"
                className="inline-flex items-center gap-1 font-medium text-[#25D366] hover:underline"
              >
                Manage templates
                <ExternalLink className="h-3.5 w-3.5" />
              </Link>
              <Link
                to="/operations/exotel-calling/settings"
                className="inline-flex items-center gap-1 font-medium text-gray-600 hover:underline dark:text-gray-300"
              >
                Exotel IVR & call settings
                <ExternalLink className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </section>

        {settings?.referenceGuide && (
          <>
            <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">How WhatsApp sending works</h3>
              <p className="mt-1 text-xs text-gray-500">
                Template variable mapping applies to <strong>Notifications</strong> (transactional) and{' '}
                <strong>Campaigns</strong> (marketing / retarget). Inbox and IVR use templates differently.
              </p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {settings.referenceGuide.sendModules.map((mod) => (
                  <div
                    key={mod.id}
                    className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-900/50"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <Link
                        to={mod.adminPath}
                        className="text-sm font-semibold text-[#25D366] hover:underline"
                      >
                        {mod.title}
                      </Link>
                      <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        {mod.category}
                      </span>
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-gray-600 dark:text-gray-400">{mod.description}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Common template variables</h3>
                  <p className="mt-1 text-xs text-gray-500">
                    Shared variable numbers for all Interakt templates. Map each slot to a context key in{' '}
                    <Link to="/operations/whatsapp/notifications" className="text-[#25D366] hover:underline">
                      Notifications
                    </Link>
                    .
                  </p>
                </div>
                <Link
                  to="/operations/whatsapp/notifications"
                  className="inline-flex items-center gap-1 text-xs font-medium text-[#25D366] hover:underline"
                >
                  Configure notifications
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              </div>
              <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-800/80">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Ref #</th>
                      <th className="px-3 py-2 text-left font-medium">Variable</th>
                      <th className="px-3 py-2 text-left font-medium">Context key</th>
                      <th className="px-3 py-2 text-left font-medium">Label</th>
                      <th className="px-3 py-2 text-left font-medium">Source</th>
                      <th className="px-3 py-2 text-left font-medium">Auto DB</th>
                      <th className="px-3 py-2 text-left font-medium">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(settings.referenceGuide.numberedSlots ?? []).map((v) => (
                      <tr key={`${v.slot}-${v.key}`} className="border-t border-gray-100 dark:border-gray-800">
                        <td className="whitespace-nowrap px-3 py-2 font-semibold text-gray-900 dark:text-white">
                          {v.slot}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 font-mono text-[#25D366]">
                          {v.templateVar}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 font-mono text-gray-800 dark:text-gray-200">
                          {v.key}
                        </td>
                        <td className="px-3 py-2">{v.label}</td>
                        <td className="px-3 py-2 capitalize text-gray-600 dark:text-gray-400">{v.sourceType}</td>
                        <td className="px-3 py-2">{v.autoFromDb ? 'Yes' : '—'}</td>
                        <td className="max-w-md px-3 py-2 text-gray-500">{v.description || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ul className="mt-4 space-y-1.5 text-xs text-gray-600 dark:text-gray-400">
                {settings.referenceGuide.mappingNotes.map((note) => (
                  <li key={note} className="flex gap-2">
                    <span className="text-gray-400">•</span>
                    <span>{note}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Wired notification events (backend)</h3>
              <p className="mt-1 text-xs text-gray-500">
                These events can send WhatsApp when enabled in{' '}
                <Link to="/operations/whatsapp/notifications" className="text-[#25D366] hover:underline">
                  Notifications
                </Link>{' '}
                with a template assigned. Other catalog events appear in Notifications but need backend wiring first.
              </p>
              <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-800/80">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Event key</th>
                      <th className="px-3 py-2 text-left font-medium">Label</th>
                      <th className="px-3 py-2 text-left font-medium">Recipient</th>
                      <th className="px-3 py-2 text-left font-medium">Trigger</th>
                      <th className="px-3 py-2 text-left font-medium">Configure in</th>
                    </tr>
                  </thead>
                  <tbody>
                    {settings.referenceGuide.wiredNotificationEvents.map((ev) => (
                      <tr key={ev.eventKey} className="border-t border-gray-100 dark:border-gray-800">
                        <td className="whitespace-nowrap px-3 py-2 font-mono text-[11px] text-gray-800 dark:text-gray-200">
                          {ev.eventKey}
                        </td>
                        <td className="px-3 py-2">{ev.label}</td>
                        <td className="px-3 py-2 capitalize">{ev.recipientType}</td>
                        <td className="max-w-xs px-3 py-2 text-gray-500">{ev.triggerDescription || '—'}</td>
                        <td className="px-3 py-2 capitalize">{ev.configureIn}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-xs text-gray-500">
                Send flow: app event →{' '}
                <code className="font-mono">notifyUser / notifyVendor</code> → enrich context from DB → build body
                values from your template mappings → Interakt API.
              </p>
            </section>
          </>
        )}
      </div>
    </WhatsAppPanelShell>
  );
};

export default WhatsAppSettingsPanel;
